import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Send, ChevronUp, ChevronDown, Sparkles, Trash2, BookOpen,
  Pencil, Undo2, Check, X as XIcon, Loader2,
} from 'lucide-react';
import { getToken, adminApi } from './api';

const HISTORY_KEY = 'svet_anya_expert_chat_v1';
const MODE_KEY = 'svet_anya_mode_v1';
const COMMAND_RE = /<<(NAVIGATE|HIGHLIGHT)\s*:\s*([^>]+?)>>/g;

// ===========================================================================
// Утилиты
// ===========================================================================
function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveHistory(h) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-30))); } catch {/* */}
}

function extractCommands(text) {
  const commands = [];
  const clean = text.replace(COMMAND_RE, (_m, kind, val) => {
    commands.push({ kind, value: val.trim() });
    return '';
  });
  return { clean, commands };
}

/**
 * Ищет один <<PROPOSAL: {json}>> в тексте. JSON может содержать вложенные {}.
 * Возвращает { clean, proposal } — proposal либо распарсенный объект, либо null.
 */
function extractProposal(text) {
  const start = text.indexOf('<<PROPOSAL:');
  if (start === -1) return { clean: text, proposal: null };
  const jsonStart = text.indexOf('{', start);
  if (jsonStart === -1) return { clean: text, proposal: null };
  let depth = 0, i = jsonStart, inStr = false, esc = false;
  for (; i < text.length; i++) {
    const ch = text[i];
    if (inStr) {
      if (esc) { esc = false; continue; }
      if (ch === '\\') { esc = true; continue; }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) { i++; break; }
    }
  }
  if (depth !== 0) return { clean: text, proposal: null };
  const jsonEnd = i;
  const closeTag = text.indexOf('>>', jsonEnd);
  if (closeTag === -1) return { clean: text, proposal: null };
  let proposal = null;
  try {
    proposal = JSON.parse(text.slice(jsonStart, jsonEnd));
  } catch { return { clean: text, proposal: null }; }
  const clean = (text.slice(0, start) + text.slice(closeTag + 2)).replace(/\s+$/, '');
  return { clean, proposal };
}

function highlightTestId(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('anya-highlight');
  setTimeout(() => el.classList.remove('anya-highlight'), 3200);
}

// ===========================================================================
// Главная панель
// ===========================================================================
export default function AnyaPanel() {
  const [collapsed, setCollapsed] = useState(true);
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || 'expert');
  const [history, setHistory] = useState(loadHistory);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [knowFilled, setKnowFilled] = useState(0);
  const [error, setError] = useState('');
  const [historyCount, setHistoryCount] = useState(0);
  const [undoing, setUndoing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [flash, setFlash] = useState(''); // короткое системное сообщение

  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // При первом монтировании и смене маршрута — знания + история правок
  useEffect(() => {
    let stopped = false;
    adminApi.knowledgeStatus()
      .then((s) => { if (!stopped) setKnowFilled(s.filled_sections || 0); })
      .catch(() => {/* ignore */});
    adminApi.contentHistory(20)
      .then((h) => { if (!stopped) setHistoryCount((h.history || []).length); })
      .catch(() => {/* ignore */});
    return () => { stopped = true; };
  }, [location.pathname]);

  // Авто-скролл вниз при новых сообщениях
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, streamingText, flash]);

  // Сохранять историю чата
  useEffect(() => { saveHistory(history); }, [history]);

  // Сохранять выбор режима
  useEffect(() => { try { localStorage.setItem(MODE_KEY, mode); } catch {/* */} }, [mode]);

  const flashMsg = (text, ms = 2400) => {
    setFlash(text);
    setTimeout(() => setFlash(''), ms);
  };

  const onClear = () => {
    if (streaming) return;
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const onSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: 'user', content: text, ts: Date.now(), mode };
    setHistory((h) => [...h, userMsg]);
    setInput('');
    setStreaming(true);
    setStreamingText('');
    setError('');

    const controller = new AbortController();
    abortRef.current = controller;

    const endpoint = mode === 'operator'
      ? '/api/admin/command'
      : '/api/admin/anya/chat';

    const body = mode === 'operator'
      ? { message: text, history: history.map((m) => ({ role: m.role, content: m.content })) }
      : { message: text, history: history.map((m) => ({ role: m.role, content: m.content })), route: location.pathname };

    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': getToken() },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error('Стрим не открылся');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';
      let fullText = '';
      const pendingCommands = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let nl;
        while ((nl = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 2);
          if (!raw.startsWith('data:')) continue;
          const data = raw.slice(5).trim();
          if (data === '[DONE]') continue;
          try {
            const obj = JSON.parse(data);
            if (obj.error) { setError(obj.error); continue; }
            if (obj.delta) {
              fullText += obj.delta;
              // Для эксперта — сразу чистим команды, чтобы Светлана не видела теги
              // Для оператора — тег PROPOSAL обычно приходит в конце, поэтому чистим только видимую часть
              if (mode === 'expert') {
                const { clean, commands } = extractCommands(fullText);
                setStreamingText(clean);
                for (const c of commands) {
                  if (!pendingCommands.some((p) => p.kind === c.kind && p.value === c.value)) {
                    pendingCommands.push(c);
                  }
                }
              } else {
                // Скрываем незавершённый PROPOSAL из видимого текста во время стрима
                const propStart = fullText.indexOf('<<PROPOSAL:');
                setStreamingText(propStart === -1 ? fullText : fullText.slice(0, propStart).trimEnd());
              }
            }
          } catch { /* skip */ }
        }
      }

      // Финальная обработка
      let finalMessage = { role: 'assistant', ts: Date.now(), mode };
      if (mode === 'operator') {
        const { clean, proposal } = extractProposal(fullText);
        finalMessage.content = clean || (proposal ? 'Готово, вот предложение:' : fullText);
        if (proposal && proposal.block_id && proposal.new_data) {
          finalMessage.proposal = proposal;
        }
      } else {
        const { clean } = extractCommands(fullText);
        finalMessage.content = clean;
      }
      setHistory((h) => [...h, finalMessage]);
      setStreamingText('');

      // Выполнить команды (только для эксперта)
      if (mode === 'expert') {
        setTimeout(() => {
          for (const c of pendingCommands) {
            if (c.kind === 'NAVIGATE' && c.value.startsWith('/admin')) navigate(c.value);
            else if (c.kind === 'HIGHLIGHT') highlightTestId(c.value);
          }
        }, 600);
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Что-то пошло не так');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, history, mode, location.pathname, navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  // Применить предложение оператора
  const applyProposal = async (msgIndex) => {
    const msg = history[msgIndex];
    if (!msg?.proposal || applying) return;
    setApplying(true);
    setError('');
    try {
      const p = msg.proposal;
      await adminApi.contentApply(p.block_id, p.new_data, p.summary || '');
      // Пометить сообщение как применённое
      setHistory((h) => h.map((m, i) => i === msgIndex ? { ...m, applied: true } : m));
      setHistoryCount((c) => c + 1);
      flashMsg('Применено — публичный сайт обновлён.');
    } catch (err) {
      setError(err.message || 'Не удалось применить');
    } finally {
      setApplying(false);
    }
  };

  // Отменить (просто скрыть карточку — proposal останется в истории для контекста)
  const declineProposal = (msgIndex) => {
    setHistory((h) => h.map((m, i) => i === msgIndex ? { ...m, declined: true } : m));
  };

  // Поправить — поставить фокус в поле и намекнуть шаблоном
  const modifyProposal = (msgIndex) => {
    setInput('Не так, поправь: ');
    setTimeout(() => inputRef.current?.focus(), 50);
    // Не помечаем — остаётся видимой на случай если Светлана передумает
  };

  const onUndoLast = async () => {
    if (undoing || historyCount === 0) return;
    setUndoing(true);
    setError('');
    try {
      const r = await adminApi.contentUndo();
      setHistoryCount((c) => Math.max(0, c - 1));
      flashMsg(`Откачено: ${r.restored?.summary || r.restored?.block_id}`);
    } catch (err) {
      if (err.status === 404) {
        setHistoryCount(0);
        flashMsg('Откатывать нечего.');
      } else {
        setError(err.message || 'Не удалось откатить');
      }
    } finally {
      setUndoing(false);
    }
  };

  const isOperator = mode === 'operator';
  const modeLabel = isOperator ? 'редактор' : 'эксперт';

  return (
    <div
      className={`anya-panel ${collapsed ? 'collapsed' : 'expanded'} ${isOperator ? 'mode-operator' : 'mode-expert'}`}
      data-testid="anya-panel"
    >
      {/* Шапка */}
      <button
        className="anya-head"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="anya-toggle"
      >
        <div className="anya-avatar">
          {isOperator ? <Pencil size={14} strokeWidth={1.8} /> : <Sparkles size={14} strokeWidth={1.8} />}
        </div>
        <div className="anya-head-text">
          <div className="anya-name">Аня · {modeLabel}</div>
          <div className="anya-sub">
            {isOperator
              ? (historyCount > 0 ? `${historyCount} правок в истории` : 'предлагает правки — вы одобряете')
              : (knowFilled === 0 ? 'жду базу знаний' : `прочитала ${knowFilled} из 7`)}
          </div>
        </div>
        {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {!collapsed && (
        <>
          {/* Тумблер режима */}
          <div className="anya-mode-row" data-testid="anya-mode-row">
            <button
              className={`anya-mode-btn ${!isOperator ? 'active' : ''}`}
              onClick={() => !streaming && setMode('expert')}
              disabled={streaming}
              data-testid="anya-mode-expert"
            >
              <Sparkles size={11} /> Эксперт
            </button>
            <button
              className={`anya-mode-btn ${isOperator ? 'active' : ''}`}
              onClick={() => !streaming && setMode('operator')}
              disabled={streaming}
              data-testid="anya-mode-operator"
            >
              <Pencil size={11} /> Редактор
            </button>
          </div>

          {/* Подсказка про базу знаний (только для эксперта) */}
          {!isOperator && knowFilled === 0 && (
            <button
              className="anya-hint"
              onClick={() => navigate('/admin/knowledge')}
              data-testid="anya-knowledge-hint"
            >
              <BookOpen size={12} />
              Заполните пару секций — я буду точнее
            </button>
          )}

          {/* История диалога */}
          <div className="anya-log" ref={scrollRef} data-testid="anya-log">
            {history.length === 0 && !streaming && (
              <div className="anya-empty">
                {isOperator
                  ? 'Скажите что поправить — «поменяй цену онлайна на 100» или «убери третий отзыв». Ничего не сохраню без вашей кнопки.'
                  : 'Привет, Светлана. Спросите меня о цифрах, попросите открыть раздел или поправить блок.'}
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} className={`anya-msg ${m.role}`} data-testid={`anya-msg-${i}`}>
                {m.content}
                {m.proposal && !m.applied && !m.declined && (
                  <div className="anya-proposal" data-testid={`anya-proposal-${i}`}>
                    <div className="anya-proposal-block">
                      блок «{blockLabel(m.proposal.block_id)}»
                    </div>
                    <div className="anya-proposal-summary" data-testid={`anya-proposal-summary-${i}`}>
                      {m.proposal.summary || 'Правка блока'}
                    </div>
                    <div className="anya-proposal-actions">
                      <button
                        className="anya-btn anya-btn-primary"
                        onClick={() => applyProposal(i)}
                        disabled={applying}
                        data-testid={`anya-proposal-apply-${i}`}
                      >
                        {applying ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                        Применить
                      </button>
                      <button
                        className="anya-btn anya-btn-ghost"
                        onClick={() => modifyProposal(i)}
                        disabled={applying}
                        data-testid={`anya-proposal-modify-${i}`}
                      >
                        Поправить
                      </button>
                      <button
                        className="anya-btn anya-btn-ghost"
                        onClick={() => declineProposal(i)}
                        disabled={applying}
                        data-testid={`anya-proposal-decline-${i}`}
                      >
                        <XIcon size={11} /> Отменить
                      </button>
                    </div>
                  </div>
                )}
                {m.proposal && m.applied && (
                  <div className="anya-proposal-status applied" data-testid={`anya-proposal-applied-${i}`}>
                    <Check size={11} /> применено
                  </div>
                )}
                {m.proposal && m.declined && (
                  <div className="anya-proposal-status declined" data-testid={`anya-proposal-declined-${i}`}>
                    <XIcon size={11} /> отменено
                  </div>
                )}
              </div>
            ))}
            {streaming && (
              <div className="anya-msg assistant streaming" data-testid="anya-msg-streaming">
                {streamingText || <span className="anya-dots"><span/><span/><span/></span>}
              </div>
            )}
            {flash && (
              <div className="anya-flash" data-testid="anya-flash">{flash}</div>
            )}
            {error && (
              <div className="anya-error" data-testid="anya-error">{error}</div>
            )}
          </div>

          {/* Ввод */}
          <div className="anya-input-row">
            <textarea
              ref={inputRef}
              className="anya-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={isOperator ? 'Что поправить?' : 'Спросите Аню…'}
              rows={1}
              disabled={streaming}
              data-testid="anya-input"
            />
            <button
              onClick={onSend}
              disabled={!input.trim() || streaming}
              className="anya-send"
              title="Отправить (Enter)"
              data-testid="anya-send"
            >
              <Send size={14} />
            </button>
          </div>

          {/* Нижняя строка: очистить + откатить (только в редакторе) */}
          <div className="anya-footer-row">
            {history.length > 0 && (
              <button onClick={onClear} className="anya-clear" data-testid="anya-clear" disabled={streaming}>
                <Trash2 size={11} /> очистить диалог
              </button>
            )}
            {isOperator && historyCount > 0 && (
              <button
                onClick={onUndoLast}
                className="anya-clear"
                data-testid="anya-undo-last"
                disabled={undoing || streaming}
                title="Откатить последнюю правку контента"
              >
                {undoing ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={11} />}
                откатить последнее
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Человеческие названия блоков — те же что в BLOCK_META на бекенде
const BLOCK_LABELS = {
  hero: 'Шапка',
  manifest: 'Манифест',
  about: 'Обо мне',
  services: 'Направления',
  how: 'Как это работает',
  reviews: 'Отзывы',
  pricing: 'Стоимость',
  contact: 'Контакты',
};
function blockLabel(id) { return BLOCK_LABELS[id] || id; }
